import React from 'react'
import PropTypes from 'prop-types'
import ReactCSSTransitionGroup from 'react-transition-group/CSSTransitionGroup'
import cssModules from 'react-css-modules'

import { getI18n } from 'client/i18n'
import { getStepName } from 'lib/route_utils'
import { hubPageUrl } from 'lib/campaign_url'

import StandardDialog from 'components/common/standard_dialog'
import ModalWizard from 'components/modal_wizard'
import Modal from 'components/common/modal'
import StandardError from 'components/common/standard_error'
import PreviewHeader from 'components/pages/campaign/preview_header'
import ModalAnimations from 'components/common/transitions/modal_animations'

import styles from './create.less'

export const TRANSITION_DURATION = 750
export const CREATION_BLOCKS = {
  byKillSwitch: 'kill_switched',
  byCountry: 'blocked',
  byAccountType: 'account_type_blocked',
  byUnconfirmed: 'unconfirmed',
}
const i18n = getI18n('components/pages/create/create')

const ModalWithAnimations = props => {
  return (
    <ModalAnimations>
      <ModalWizard {...props} />
    </ModalAnimations>
  )
}

class CreatePage extends React.Component {
  static propTypes = {
    children: PropTypes.any,
    params: PropTypes.object,
    location: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    routes: PropTypes.array.isRequired,
    blockType: PropTypes.oneOf(Object.values(CREATION_BLOCKS)),
    countryName: PropTypes.string,
    creationError: PropTypes.object,
    hideClose: PropTypes.bool,
    email: PropTypes.string,
    operatingRemotely: PropTypes.bool,
  }

  state = {
    showCloseDialog: false,
  }

  handleClose = () => {
    const currentStep = getStepName('create', this.props.routes)
    if (
      currentStep === 'basics' ||
      currentStep === 'publish' ||
      currentStep === 'share/:campaignId'
    ) {
      this.handleCloseDialogOk()
    } else {
      this.setState({ showCloseDialog: true })
    }
  }

  closeDialog = () => {
    this.setState({ showCloseDialog: false })
  }

  handleCloseDialogOk = () => {
    if (document.referrer.indexOf('/create') > -1) {
      // sometimes the referrer is the same page
      location.href = hubPageUrl()
    } else {
      location.href = document.referrer || hubPageUrl()
    }
  }

  getErrorComponent = errorType => {
    const shouldShowButton =
      errorType === CREATION_BLOCKS.byAccountType ||
      errorType === CREATION_BLOCKS.byUnconfirmed

    return (
      <Modal onClose={this.handleClose}>
        <StandardError
          iconType="warning"
          title={i18n(`${errorType}_title`)}
          subtitle={i18n(`${errorType}_subtitle`, {
            country: this.props.countryName,
            email: this.props.email,
          })}
          showButton={shouldShowButton}
          backButton={shouldShowButton}
          extraContent={
            errorType === CREATION_BLOCKS.byUnconfirmed ? (
              <a styleName="settings-link" href="/myaccount/settings">
                {i18n('unconfirmed_settings_link')}
              </a>
            ) : (
              undefined
            )
          }
        />
      </Modal>
    )
  }

  render() {
    const child = React.Children.only(this.props.children)
    let stepName = this.props.params.nextStep
    let prevStepName = this.props.params.prevStep
    const isInitialState =
      this.props.location.state && this.props.location.state.initial

    let key
    let WrappingComponent
    if (stepName === 'preview') {
      WrappingComponent = PreviewHeader
      key = 'preview'
    } else {
      WrappingComponent = ModalWithAnimations
      key = 'wizard'
    }

    const movingForward = this.props.location.action !== 'POP'
    let transitionNames
    let animateModal
    if (prevStepName === 'preview') {
      animateModal = false
      transitionNames = {
        enter: styles['create-modal-enter'],
        enterActive: styles['create-modal-enter-active'],
        leave: styles['preview-leave'],
        leaveActive: styles['preview-leave-active'],
      }
    } else if (stepName === 'preview') {
      animateModal = false
      transitionNames = {
        enter: styles['preview-enter'],
        enterActive: styles['preview-enter-active'],
        leave: styles['create-modal-leave'],
        leaveActive: styles['create-modal-leave-active'],
      }
    } else {
      animateModal = true
      transitionNames = {}
    }

    if (this.props.blockType) {
      return this.getErrorComponent(this.props.blockType)
    }

    return (
      <div>
        {this.state.showCloseDialog && (
          <StandardDialog
            size="medium"
            title={i18n('close_dialog_title')}
            subtitle={i18n('close_dialog_subtitle')}
            closeButtonTitle={i18n('keep_creating')}
            okButtonTitle={i18n('quit')}
            onClose={this.closeDialog}
            onOk={this.handleCloseDialogOk}
          />
        )}
        <ReactCSSTransitionGroup
          transitionName={transitionNames}
          transitionEnterTimeout={TRANSITION_DURATION}
          transitionLeaveTimeout={TRANSITION_DURATION}
          component="div"
          styleName="preview-animation-container"
        >
          <WrappingComponent
            key={key}
            movingForward={movingForward}
            hideBack={
              (stepName === 'publish' && !this.props.creationError) ||
              isInitialState
            }
            hideClose={this.props.hideClose}
            onClose={this.handleClose}
            animate={animateModal}
            showSpinner={this.props.operatingRemotely}
          >
            {React.cloneElement(child, { key: stepName })}
          </WrappingComponent>
        </ReactCSSTransitionGroup>
      </div>
    )
  }
}

export default cssModules(CreatePage, styles)
